/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud
 * (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */


define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../lib/@waca/core-client/js/core-client/ui/KeyCodes',
	'underscore',
	'jquery',
	'doT',
	'text!./templates/LoadingIndicator.html',
	'react',
	'react-dom',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'../../app/nls/StringResources',
	'./WidgetsContentContainer'
], function(View, KeyCodes, _, $, dot, LoadingIndicatorTemplate, React, ReactDOM, PropTypes, UI_Toolkit, StringResources, WidgetsContentContainer) {

	const { Component } = React;
	const { Container, ProgressIndicator, Tabs, TabPanel , VSpacer, Label, FlexLayout, FlexItem } = UI_Toolkit;
	const DEFAULT_SELECTED_TAB = 'Basics';

	class WidgetsTabs extends Component {
		constructor (...args) {
			super(...args);
			this.state = {
				selectedTab: this.props.selectedTab,
				deleteable: false,
				editable: false,
				addable: false,
				align: 'left',
				tabs: this.props.tabs
			};
		}

		render () {
			const panels = this._renderTabPanels();
			const tabsClassNames = panels.length === 1 ? 'widgets-panel-tabs hide-tabs' : 'widgets-panel-tabs';
			return (
				<Container className='widgets-panel-container'>
					<VSpacer size={2} />
					<Label className='widgets-panel-banner' label={StringResources.get('widgets')}/>
					<VSpacer size={2} />
					<Tabs className={tabsClassNames}
						selected={this.state.selectedTab}
						onChange={this.onChange.bind(this)}
					>
						{panels}
					</Tabs>
				</Container>
			);
		}

		_renderTabPanels() {
			const panels = [];
			panels.push(
				<TabPanel
					id={DEFAULT_SELECTED_TAB}
					label={StringResources.get('widgetsPanelBasicsTab')}
				>
					{this._renderBasicsPanel()}
				</TabPanel>
			);
			const { images } = this.props.tabs.items;
			if (images && images.length) {
				panels.push(<TabPanel
					id={'ImagesTab'}
					label={StringResources.get('widgetsPanelImageLibraryTab')}
				>
					{this._renderImageWidgets(images)}
				</TabPanel>);
			}
			return panels;
		}

		_renderBasicsPanel() {
			if (this.state.selectedTab === DEFAULT_SELECTED_TAB) {
				const { widgets, advancedWidgets, shapes } = this.props.tabs.items;
				const standardSection = this._renderSection('standard', StringResources.get('widgetsPanelSectionStandard'), widgets);
				const advancedSection = this._renderSection('advanced', StringResources.get('widgetsPanelSectionAdvanced'), advancedWidgets);
				const shapesSection = this._renderSection('shapes', StringResources.get('widgetsPanelSectionShapes'), shapes);
				return (
					<div>
						{standardSection}
						{advancedSection}
						{shapesSection}
					</div>
				);
			}
			return null;
		}

		_renderSection(sectionName, sectionLabel, items) {
			if (items && items.length) {
				const sectionClassName = `widgets-${sectionName}-container`;
				const bannerClassName = `widgets-${sectionName}-banner`;
				const iconContainerClassName = `widgets-${sectionName}-icons-container`;
				return (
					<Container className={sectionClassName}>
						<VSpacer size={3} />
						<Label className={bannerClassName} label={sectionLabel}/>
						{this._renderWidgets(iconContainerClassName, items)}
					</Container>
				);
			}
			return null;
		}

		_renderImageWidgets() {
			if (this.state.selectedTab === 'ImagesTab') {
				const imageWidgets = this._renderWidgets('widgets-images-icons-container', this.props.tabs.items.images);
				return (
					<Container className='widgets-images-container'>
						{imageWidgets}
					</Container>
				);
			}
			return null;
		}

		_renderWidgets(containerClassName, widgetItems) {
			return (
				<WidgetsContentContainer
					containerClass={containerClassName}
					itemClass='prop-item'
					items={widgetItems}
					handlers={this.props.tabs.handlers}
				/>
			);
		}

		onChange (tabId) {
			this.setState({
				selectedTab: tabId
			});
		}

		_renderProgressIndicator () {
			return (
				<FlexLayout
					fullHeight
					direction="column"
					justifyContent='center'
					width='100%'
					height='100%'
				>
					<FlexItem alignSelf='center'>
						<ProgressIndicator />
					</FlexItem>
				</FlexLayout>
			);
		}
	}

	WidgetsTabs.propTypes = {
		tabs: PropTypes.object.isRequired,
		dashboardApi: PropTypes.object.isRequired,
		selectedTab: PropTypes.string.isRequired
	};

	WidgetsTabs.defaultProps = {
		selectedTab: DEFAULT_SELECTED_TAB
	};

	const _getWidgetItemSelector = (classSelectors) => {
		return _.chain(classSelectors).map( selector => {
			return '.'+ selector;
		}).value().join(' ');
	};

	return class WidgetsPanelView extends View {

		constructor (...args) {
			super(...args);
			const options = args[0];
			this.getEntries = options.getEntries;
			this.selectedTab = options.selectedTab || DEFAULT_SELECTED_TAB;
			this.dashboardApi = options.dashboardApi;
			this.glassContext = options.glassContext;
			this.dndManager = this.dashboardApi.getFeature('DashboardDnd.internal');
			this.loadingIndicatorTemplate = dot.template(LoadingIndicatorTemplate || '');
			this.viewState = {};
		}

		init (...args) {
			this.events = {
				'clicktap .widgets-panel-container .prop-item': '_onItemClick',
				'mousedown .widgets-panel-container .prop-item': '_onItemMouseDownStartDrag',
				'keydown .widgets-panel-container .prop-item': '_onItemKeyDown'
			};
			super.init(...args);
		}

		remove(...args) {
			if(this._dropZone) {
				this._dropZone.remove();
				this._dropZone = null;
			}
			this.dndManager = null;
			ReactDOM.unmountComponentAtNode(this.$el[0]);
			super.remove(...args);
		}

		render() {
			this._showLoadingIndicator();

			return this.getEntries().then(data => {
				this.items = data.allItems;
				this.options = data.options;
				return data.items;
			}).then((items) => {

				this.viewState.handlers = {
					componentDidMountCallback: this._componentDidMountCallback.bind(this),
					componentWillUnmountCallback: this._componentWillUnmountCallback.bind(this)
				};
				this.viewState.items = items;

				ReactDOM.render(
					<WidgetsTabs
						tabs={this.viewState}
						dashboardApi={this.dashboardApi}
						selectedTab={this.selectedTab}
					/>,
					this.$el[0]
				);
			}).finally(() => {
				this._hideLoadingIndicator();
				this._dropZone = this.dndManager.addDropTarget(this.$el[0], { accepts: this._accepts.bind(this) });
			});
		}

		_showLoadingIndicator () {
			this.$el.append(this.loadingIndicatorTemplate({}));
			this.loadingIndicatorContainer = this.$el.find('.loadingIndicatorContainer');
			this.loadingIndicator = this.loadingIndicatorContainer.find('.loading_indicator');
			ReactDOM.render(
				<ProgressIndicator/>,
				this.loadingIndicator.get(0)
			);
		}

		_hideLoadingIndicator () {
			if (this.loadingIndicatorContainer) {
				this.loadingIndicatorContainer.remove();
				this.loadingIndicatorContainer = null;
				ReactDOM.unmountComponentAtNode(this.loadingIndicator.get(0));
			}
		}

		_accepts () {
			return true;
		}

		_componentDidMountCallback (containerSelector, visItemSelector) {
			const onItemHoldAndStartDrag = this._onItemHoldAndStartDrag.bind(this);
			const selector = _getWidgetItemSelector([containerSelector, visItemSelector]);
			const $items = this.$el.find(selector);
			for (let i = 0; i < $items.length; ++i) {
				const $item = $($items[i]);
				$item.hammer();
				$item.on('hold', onItemHoldAndStartDrag);
			}
		}

		_componentWillUnmountCallback (containerSelector, visItemSelector) {
			const onItemHoldAndStartDrag = this._onItemHoldAndStartDrag.bind(this);
			const selector = _getWidgetItemSelector([containerSelector, visItemSelector]);
			const $items = this.$el.find(selector);
			for (let i = 0; i < $items.length; ++i) {
				const $item = $($items[i]);
				$item.off('hold', onItemHoldAndStartDrag);
			}
		}

		_getEntry (event) {
			const $node = $(event.target);
			let $target = $node.closest('.prop-item');
			const targetName = $target.data('name');
			const targetItem = this.items.find(item => {
				return item.name === targetName;
			});

			//The content contains the svg that only gets use for Drag and Drop
			return targetItem.content;
		}

		_onItemKeyDown (event) {
			if (event.keyCode === KeyCodes.ENTER || event.keyCode === KeyCodes.SPACE) {
				this._onItemClick(event);
			}
		}

		_onItemClick (event) {
			const currentTarget = event.currentTarget;
			if ($.data(currentTarget, '_clickEventReceived') !== true) {
				// Guard against adding two widgets at once during a double click.
				$.data(currentTarget, '_clickEventReceived', true);
				setTimeout(function () {
					$.data(currentTarget, '_clickEventReceived', false);
				}, 500);
				if (this.options.onItemClick) {
					const entry = this._getEntry(event);
					this._setDashboardApi(entry);
					this.options.onItemClick(entry, event);
					if (event && event.gesture) {
						event.gesture.preventDefault();
					}
				}
			}
		}

		_onStartDrag (event) {
			if (this.options.onItemStartDrag) {
				if (event.preventDefault) {
					// prevent default drag behavior
					event.preventDefault();
				}
				const entry = this._getEntry(event);
				this._setDashboardApi(entry);
				this.options.onItemStartDrag(entry, event);
			}
		}

		_setDashboardApi(widgetEntry) {
			//always fetch the DashboardAPI from the current content view
			//TODO: pass dashboardAPI from event handler inside WidgetsPanelHandler.js
			if(!widgetEntry.options){
				widgetEntry.options = {};
			}
			widgetEntry.options.dashboardApi = this.glassContext.appController.getCurrentContentView().getDashboardApi();
		}

		_onItemHoldAndStartDrag (event) {
			event.showAvatarImmediately = true;
			this._onStartDrag(event);
		}

		_onItemMouseDownStartDrag (event) {
			this._onStartDrag(event);
		}

	};
});
